import {
  BuildingOffice2Icon,
  EnvelopeIcon,
  PhoneIcon,
} from "@heroicons/react/24/solid";
import { cx } from "cva";
import { BuildHTML } from "~/components/BuildHtml/BuildHtml";
import { FeatureWithDemo } from "~/components/LandingPage/schemas";
import { DemoForm } from "~/landing-pages/components/Demo/DemoForm";
import {
  backgroundColorStyle,
  primaryTextColorStyle,
  secondaryTextColorStyle,
} from "~/landing-pages/shared-landing-page-styles";
import { tagToClassNameMap } from "~/landing-pages/utils/tagToClassNameMap";
import { RequestDemoFormSchema } from "~/lib/shared/schemas/requestDemoSchema";
import { Body, Headline, Icon } from "~/ui-library";

export type FeatureWithDemoSplitProps = Omit<
  FeatureWithDemo,
  "layout" | "successMessage" | "successRedirectUrl"
> & {
  appearance: "dark" | "light";
  onFormSubmit: (values: RequestDemoFormSchema) => void;
};

const CompanyContactInformation = ({
  appearance,
  className,
}: {
  appearance: "dark" | "light";
  className?: string;
}) => (
  <dl
    className={cx(
      "space-y-4 text-body-large leading-body",
      secondaryTextColorStyle({ appearance }),
      className
    )}
  >
    <div className="flex gap-x-4">
      <dt className="flex-none">
        <span className="sr-only">Address</span>
        <Icon
          className="h-7 w-6 text-gray-400"
          SvgIcon={BuildingOffice2Icon}
          size="small"
          height={20}
          width={20}
        />
      </dt>
      <dd>
        1025 F St NW
        <br />
        Washington, DC 20004
      </dd>
    </div>
    <div className="flex gap-x-4">
      <dt className="flex-none">
        <span className="sr-only">Telephone</span>
        <Icon
          className="h-7 w-6 text-gray-400"
          SvgIcon={PhoneIcon}
          size="small"
          height={20}
          width={20}
        />
      </dt>
      <dd>
        <a
          className={secondaryTextColorStyle({
            appearance,
            className: "underline underline-offset-4",
          })}
          href="tel:+1 (202) 506-1957"
        >
          +1 (202) 506-1957
        </a>
      </dd>
    </div>
    <div className="flex gap-x-4">
      <dt className="flex-none">
        <span className="sr-only">Email</span>
        <Icon
          className="h-7 w-6 text-gray-400"
          SvgIcon={EnvelopeIcon}
          size="small"
          height={20}
          width={20}
        />
      </dt>
      <dd>
        <a
          className={secondaryTextColorStyle({
            appearance,
            className: "underline underline-offset-4",
          })}
          href="mailto:pro@morningconsult.com"
        >
          pro@morningconsult.com
        </a>
      </dd>
    </div>
  </dl>
);

export const FeatureWithDemoSplit: React.FC<FeatureWithDemoSplitProps> = ({
  title,
  description,
  appearance,
  onFormSubmit,
  ...formProps
}) => (
  <div className={cx("relative isolate", backgroundColorStyle({ appearance }))}>
    <div className="mx-auto grid max-w-5xl grid-cols-1 gap-16 px-6 py-4 sm:py-6 lg:grid-cols-[auto_minmax(0,1fr)] lg:p-8">
      <div className="flex flex-col gap-0 md:flex-row md:justify-around md:gap-16 lg:flex-col lg:justify-start lg:gap-0">
        <div>
          <Headline
            as="h2"
            level={1}
            className={cx(primaryTextColorStyle({ appearance }), "font-bold ")}
          >
            {title}
          </Headline>
          <Body
            size="large"
            className={cx(secondaryTextColorStyle({ appearance }), "mt-2")}
          >
            <BuildHTML
              html={description}
              tagToClassNameMap={tagToClassNameMap}
            />
          </Body>
        </div>
        <div className="mt-6 self-start md:mt-0 md:self-center lg:mt-6 lg:self-start">
          <CompanyContactInformation appearance={appearance} />
        </div>
      </div>
      <DemoForm
        {...formProps}
        appearance={appearance}
        className="mt-10 lg:mt-0"
        onFormSubmit={onFormSubmit}
      />
    </div>
  </div>
);
